import { X } from '@phosphor-icons/react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import React, {
  createContext,
  ReactNode,
  useCallback, useContext, useState
} from 'react';

interface DialogProps {
  title: string;
  subtitle?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  destructive?: boolean;
}

interface DialogState extends DialogProps {
  isOpen: boolean;
  resolve?: (confirmed: boolean) => void;
}

interface DialogContextType {
  showDialog: (props: Omit<DialogProps, 'onConfirm' | 'onCancel'>) => Promise<boolean>;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [dialogState, setDialogState] = useState<DialogState>({
        title: '',
        isOpen: false,
    });

    const showDialog = useCallback((props: Omit<DialogProps, 'onConfirm' | 'onCancel'>): Promise<boolean> => {
        return new Promise((resolve) => {
            setDialogState({
                ...props,
                isOpen: true,
                resolve,
            });
        });
    }, []);

    const handleConfirm = useCallback(() => {
        const { onConfirm, resolve } = dialogState;
        onConfirm?.();
        resolve?.(true);
        setDialogState(prev => ({ ...prev, isOpen: false }));
    }, [dialogState]);

    const handleCancel = useCallback(() => {
        const { onCancel, resolve } = dialogState;
        onCancel?.();
        resolve?.(false);
        setDialogState(prev => ({ ...prev, isOpen: false }));
    }, [dialogState]);

    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) {
            handleCancel();
        }
    }, [handleCancel]);

    return (
        <DialogContext.Provider value={{ showDialog }}>
            {children}
            {dialogState.isOpen ? (
                <AlertDialog.Root open={dialogState.isOpen} onOpenChange={handleOpenChange}>
                    <AlertDialog.Portal>
                        <AlertDialog.Overlay className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-sm animate-in fade-in duration-200 overflow-y-auto flex items-center justify-center p-4" />
                        <AlertDialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[1001] w-full min-w-[400px] max-w-[448px] bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 flex flex-col focus:outline-none">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                <AlertDialog.Title className="text-lg font-bold text-text-main">
                                    {dialogState.title}
                                </AlertDialog.Title>
                                <AlertDialog.Cancel asChild>
                                    <button
                                        className="p-1 text-text-sub hover:text-text-main hover:bg-gray-100 rounded-full transition-colors"
                                    >
                                        <X size={20} weight="bold" />
                                    </button>
                                </AlertDialog.Cancel>
                            </div>
                            {dialogState.subtitle ? (
                                <div className="p-6 space-y-4">
                                    <AlertDialog.Description className="text-sm text-text-sub leading-relaxed">
                                        {dialogState.subtitle}
                                    </AlertDialog.Description>
                                </div>
                            ) : null}
                            <div className="flex gap-3 justify-end px-6 py-4">
                                <AlertDialog.Cancel asChild>
                                    <button
                                        className="px-4 py-2 text-sm font-bold text-text-sub hover:bg-gray-100 rounded-xl transition-colors"
                                    >
                                        {dialogState.cancelText || 'Cancel'}
                                    </button>
                                </AlertDialog.Cancel>
                                <AlertDialog.Action asChild>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          handleConfirm();
                                      }}
                                        className={`px-6 py-2 text-sm font-bold text-white rounded-xl shadow-sm transition-colors ${
                                          dialogState.destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-text-main hover:bg-text-main/90'
                                      }`}
                                    >
                                        {dialogState.confirmText || 'Confirm'}
                                    </button>
                                </AlertDialog.Action>
                            </div>
                        </AlertDialog.Content>
                    </AlertDialog.Portal>
                </AlertDialog.Root>
            ) : null}
        </DialogContext.Provider>
    );
};

export const useDialog = () => {
    const context = useContext(DialogContext);
    if (context === undefined) {
        throw new Error('useDialog must be used within a DialogProvider');
    }
    return context;
};
